import { motion } from 'framer-motion';
import { CreditCard, Landmark, PiggyBank, Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  Button,
  Card,
  ListRow,
  SectionHeader,
  SwipeRow,
  useToast,
} from '../../components';
import { useActions, useAppData } from '../../data';
import type { Account, AccountType } from '../../data/types';
import { formatCurrency } from '../../lib/currency';
import { AccountSheet } from './AccountSheet';

const TYPE_LABELS: Record<AccountType, string> = {
  checking: 'Checking',
  savings: 'Savings',
  credit: 'Credit',
};

function typeIcon(type: AccountType): ReactNode {
  switch (type) {
    case 'checking':
      return <Landmark size={18} strokeWidth={1.5} />;
    case 'savings':
      return <PiggyBank size={18} strokeWidth={1.5} />;
    case 'credit':
      return <CreditCard size={18} strokeWidth={1.5} />;
  }
}

function typeIconClasses(type: AccountType): {
  iconBgClass: string;
  iconColorClass: string;
} {
  switch (type) {
    case 'checking':
      return { iconBgClass: 'bg-accent/10', iconColorClass: 'text-accent' };
    case 'savings':
      return { iconBgClass: 'bg-positive/15', iconColorClass: 'text-positive' };
    case 'credit':
      return { iconBgClass: 'bg-negative/15', iconColorClass: 'text-negative' };
  }
}

/**
 * Manual account balances — visibility layer only. Users type their
 * current balances themselves; budget math never reads this. The list
 * is surfaced on the Today tab via CashPositionCard.
 */
export function ManualAccountsSection() {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();
  const [editing, setEditing] = useState<'add' | Account | null>(null);

  const accounts = data.accounts ?? [];

  return (
    <motion.div>
      <SectionHeader title="Accounts" />
      {accounts.length === 0 ? (
        <Card elevation="sm" className="mb-section-gap">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-button bg-surface-secondary inline-flex items-center justify-center text-text-secondary shrink-0">
              <Landmark size={20} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-text-primary">
                Add a balance
              </div>
              <p className="text-[12px] text-text-secondary leading-snug">
                Add your checking or savings to see your cash position.
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            fullWidth
            leadingIcon={<Plus size={16} strokeWidth={1.5} />}
            onClick={() => setEditing('add')}
          >
            Add account
          </Button>
        </Card>
      ) : (
        <Card flush elevation="sm" className="mb-section-gap py-1">
          {accounts.map((a, i) => {
            const tone = typeIconClasses(a.type);
            return (
              <div key={a.id}>
                {i > 0 ? (
                  <div className="h-px bg-surface-secondary mx-card-pad" />
                ) : null}
                <SwipeRow
                  onDelete={() => {
                    actions.deleteAccount(a.id);
                    toast.show({ message: 'Account removed', kind: 'info' });
                  }}
                  onTap={() => setEditing(a)}
                >
                  <ListRow
                    icon={typeIcon(a.type)}
                    iconBgClass={tone.iconBgClass}
                    iconColorClass={tone.iconColorClass}
                    title={a.name || TYPE_LABELS[a.type]}
                    subtitle={TYPE_LABELS[a.type]}
                    value={formatCurrency(a.balance, {
                      currency: data.profile.currency,
                    })}
                    accessory="chevron"
                  />
                </SwipeRow>
              </div>
            );
          })}
          <div className="h-px bg-surface-secondary mx-card-pad" />
          <div className="px-card-pad py-2">
            <Button
              variant="ghost"
              fullWidth
              size="sm"
              leadingIcon={<Plus size={16} strokeWidth={1.5} />}
              onClick={() => setEditing('add')}
            >
              Add account
            </Button>
          </div>
        </Card>
      )}

      <AccountSheet
        mode={editing}
        currency={data.profile.currency}
        onClose={() => setEditing(null)}
        onSave={(payload) => {
          if (payload.mode === 'add') {
            actions.addAccount(payload.input);
            toast.show({ message: 'Account added', kind: 'success' });
          } else {
            actions.updateAccount(payload.id, payload.patch);
            toast.show({ message: 'Account updated', kind: 'success' });
          }
          setEditing(null);
        }}
      />
    </motion.div>
  );
}
